import React from 'react';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/lib/upload';

import { FileModel } from '../../../../../../gamma-api';
import { formatFileSize } from '../../../../../utils/fileUtil';
import IbSpin from '../../../../../components/common/IbSpin';
import IbTable from '../../../../../components/common/IbTable';
import IbIcon from '../../../../../components/common/IbIcon';
import IbButton from '../../../../../components/common/IbButton';

import './index.less';
import { MAIN_CLASS_NAME, NAME_CLASS_NAME } from './const';

const EMPTY_VALUE = '-';

export enum FileTableStore {
  Client = 'Client',
  Server = 'Server',
}

export interface IFileTableItem {
  client?: RcFile;
  server?: FileModel;
  processing: boolean;
}

export interface IFileTableProps {
  loading: boolean;
  items: IFileTableItem[];
  onDelete?: (file: IFileTableItem) => void;
}

export const areFilesEqual = (item1: IFileTableItem, item2: IFileTableItem): boolean => {
  if (item1.server && item2.server) {
    return item1.server.file_id === item2.server.file_id;
  }

  if (item1.client && item2.client) {
    return item1.client.uid === item2.client.uid;
  }

  return false;
};

const FileTable: React.FC<IFileTableProps> = ({ loading, items, onDelete }) => {
  const { t } = useTranslation();

  const getRowKey = (item: IFileTableItem): string => {
    return item.server?.file_id || item.client?.uid || EMPTY_VALUE;
  };

  const getDisplayName = (item: IFileTableItem): string => {
    return item.server?.name || item.client?.name || EMPTY_VALUE;
  };

  const getContentUrl = (item: IFileTableItem): string => {
    return item.server?.urls.public || '';
  };

  const getContentSize = (item: IFileTableItem): number => {
    return item.server?.size || item.client?.size || 0;
  };

  const renderName = (item: IFileTableItem) => {
    const name = getDisplayName(item);
    const url = getContentUrl(item);

    if (!url) {
      return <span className={NAME_CLASS_NAME}>{name}</span>;
    }

    return (
      <a className={NAME_CLASS_NAME} href={url} rel="noreferrer" target="_blank">
        {name}
      </a>
    );
  };

  const renderSize = (item: IFileTableItem) => {
    const size = getContentSize(item);
    return formatFileSize(size);
  };

  const renderButton = (item: IFileTableItem) => {
    if (item.processing) {
      return (
        <IbButton type="icon">
          <IbSpin />
        </IbButton>
      );
    }

    const onButtonClick = () => onDelete?.(item);

    return <IbButton icon={<IbIcon iconName="delete" />} type="icon" onClick={onButtonClick} />;
  };

  const columns = [
    {
      title: t('File table: Name'),
      render: renderName,
      width: '80%',
      ellipsis: true,
    },
    {
      title: t('File table: Size'),
      render: renderSize,
      width: '20%',
    },
    {
      title: '',
      render: renderButton,
      width: 40,
    },
  ];

  return (
    <IbTable
      className={MAIN_CLASS_NAME}
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey={getRowKey}
      size="middle"
    />
  );
};

export default FileTable;
